import React, { useEffect, useMemo, useState } from "react";
import { BookOpenText, Building2, ChevronDown, ChevronLeft, ChevronRight, GraduationCap, Pencil, Plus, Search, Sparkles, Trash2, University } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { axiosReq } from "../../commons/axiosReq";
import { DeleteModalWriters } from "../../components";
import AddModalCollegeField from "../../components/MainAdmin/Modals/AddModalCollegeField";
import CollegeFieldImporter from "../../components/MainAdmin/CollegeFieldImporter";
import "./MainAdminCollegeFields.css";
import "./MainAdminCollegesImporter.css";

const MainAdminCollegeFields = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedCollegeId = Number(state?.id) || null;
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [row, setRow] = useState(10);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!new Cookies().get("token")) navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const loadOptions = async () => {
      const [fieldResult, collegeResult] = await Promise.all([
        axiosReq("Field/GetFields", "get"),
        axiosReq("College/GetColleges", "get"),
      ]);
      setFields((Array.isArray(fieldResult) ? fieldResult : []).map((item) => ({ value: item.fieldId, label: item.name })));
      setColleges((Array.isArray(collegeResult) ? collegeResult : []).map((item) => ({
        value: item.collegeId,
        label: item.name,
        universityId: item.universityId,
        universityName: item.university?.name || ""
      })));
    };
    loadOptions();
  }, []);

  useEffect(() => {
    let active = true;
    const loadItems = async () => {
      setLoading(true);
      setError("");
      const query = `page=${page}&pageSize=${row}&search=${encodeURIComponent(search.trim())}`;
      const response = selectedCollegeId
        ? await axiosReq(`CollegeField/GetCollegeFieldsByCollege?${query}`, "post", { collegeId: selectedCollegeId })
        : await axiosReq(`CollegeField/GetCollegeFields?${query}`, "get");
      if (!active) return;
      const result = response?.data || response;
      if (!result || result === "BadRequest") {
        setItems([]);
        setCount(0);
        setError("دریافت رشته‌های دانشکده با خطا روبه‌رو شد.");
      } else {
        setItems(Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : []);
        setCount(Number(result?.count ?? (Array.isArray(result) ? result.length : 0)));
      }
      setLoading(false);
    };
    loadItems();
    return () => { active = false; };
  }, [page, row, search, reloadKey, selectedCollegeId]);

  const selectedCollegeName = useMemo(() => {
    const fromItem = items.find((item) => item.collegeId === selectedCollegeId)?.college?.name;
    return fromItem || colleges.find((item) => item.value === selectedCollegeId)?.label || "";
  }, [items, colleges, selectedCollegeId]);

  const openEdit = (item) => {
    setSelectedItem({ ...item, id: item.collegeFieldId });
    setShowEditModal(true);
  };
  const openDelete = (item) => {
    setSelectedItem({ ...item, id: item.collegeFieldId });
    setShowDeleteModal(true);
  };
  const refresh = () => setReloadKey((value) => value + 1);
  const start = count === 0 ? 0 : (page - 1) * row + 1;
  const end = Math.min(page * row, count);

  return <div className="college-fields-page" dir="rtl">
    <header className="college-fields-header">
      <div className="college-fields-heading">
        <span className="college-fields-heading-icon"><BookOpenText size={22} /></span>
        <div><h1>رشته‌های دانشکده</h1><p>{selectedCollegeId ? `رشته‌های ثبت‌شده برای ${selectedCollegeName || "دانشکده انتخاب‌شده"}` : "نمای یکپارچه رشته‌ها، دانشکده‌ها و استادهای متصل"}</p></div>
      </div>
      <div className="college-fields-toolbar">
        <label className="college-fields-search"><Search size={18} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="جستجو در رشته، دانشکده یا دانشگاه..." /></label>
        <button className="college-fields-add" onClick={() => setShowAddModal(true)}><Plus size={19} />افزودن رشته به دانشکده</button>
      </div>
    </header>

    <section className="college-fields-summary">
      <div><span><BookOpenText size={19} /></span><p>کل رشته‌های دانشکده</p><strong>{count.toLocaleString("fa-IR")}</strong></div>
      <div><span><Building2 size={19} /></span><p>دامنه نمایش</p><strong className="summary-text">{selectedCollegeName || "همه دانشکده‌ها"}</strong></div>
      <div><span><GraduationCap size={19} /></span><p>استادهای صفحه جاری</p><strong>{items.reduce((sum, item) => sum + Number(item.teacherCount || 0), 0).toLocaleString("fa-IR")}</strong></div>
    </section>

    <section className={`colleges-import ${showImporter ? "is-open" : ""}`}>
      <button type="button" className="colleges-import-toggle" onClick={() => setShowImporter((value) => !value)}>
        <span><Sparkles size={18} /><span><strong>Importer رشته‌های دانشکده</strong><small>{selectedCollegeId ? `پیدا کردن و ثبت رشته برای ${selectedCollegeName || "دانشکده انتخاب‌شده"}` : "انتخاب دانشکده و دریافت رشته‌های پیشنهادی از OpenAlex"}</small></span></span>
        <b>{showImporter ? "بستن" : "باز کردن"}<ChevronDown size={16} /></b>
      </button>
      {showImporter && <CollegeFieldImporter colleges={colleges} initialCollegeId={selectedCollegeId} onChanged={refresh} />}
    </section>

    {loading ? <div className="college-fields-state">در حال دریافت اطلاعات...</div> : error ? <div className="college-fields-state is-error">{error}<button onClick={refresh}>تلاش دوباره</button></div> : items.length === 0 ? <div className="college-fields-state"><BookOpenText size={36} /><strong>رشته‌ای پیدا نشد</strong><span>می‌توانید رشته جدیدی به یک دانشکده اضافه کنید.</span></div> :
      <section className="college-fields-grid">
        {items.map((item) => <article className="college-field-card" key={item.collegeFieldId}>
          <div className="college-field-card-top">
            <span className="college-field-card-icon"><BookOpenText size={22} /></span>
            <div><small>رشته تحصیلی</small><h2>{item.field?.name || "بدون نام"}</h2></div>
            <span className="college-field-teacher-count"><GraduationCap size={15} />{Number(item.teacherCount || 0).toLocaleString("fa-IR")} استاد</span>
          </div>
          <div className="college-field-path">
            <p><Building2 size={16} /><span><small>دانشکده</small>{item.college?.name || "ثبت نشده"}</span></p>
            <i />
            <p><University size={16} /><span><small>دانشگاه</small>{item.college?.university?.name || "ثبت نشده"}</span></p>
          </div>
          <div className="college-field-card-actions">
            <button className="primary" onClick={() => navigate(`/mainAdmin/collegeField/${item.collegeFieldId}`)}><GraduationCap size={17} />مشاهده استادها</button>
            <button onClick={() => openEdit(item)} aria-label="ویرایش"><Pencil size={17} /></button>
            <button className="danger" onClick={() => openDelete(item)} aria-label="حذف"><Trash2 size={17} /></button>
          </div>
        </article>)}
      </section>}

    <footer className="college-fields-pagination">
      <label>تعداد در صفحه<select value={row} onChange={(event) => { setRow(Number(event.target.value)); setPage(1); }}><option value={5}>۵</option><option value={10}>۱۰</option><option value={25}>۲۵</option></select></label>
      <span>{start.toLocaleString("fa-IR")} تا {end.toLocaleString("fa-IR")} از {count.toLocaleString("fa-IR")}</span>
      <div><button disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronRight size={18} /></button><b>{page.toLocaleString("fa-IR")}</b><button disabled={end >= count} onClick={() => setPage((value) => value + 1)}><ChevronLeft size={18} /></button></div>
    </footer>

    {showAddModal && <AddModalCollegeField id={selectedCollegeId} colleges={colleges} fields={fields} title="افزودن رشته جدید" buttonName="ثبت" setShowModal={setShowAddModal} reCheck={reloadKey} setRecheck={refresh} setData={setSelectedItem} />}
    {showEditModal && <AddModalCollegeField id={selectedItem?.collegeId || selectedCollegeId} colleges={colleges} fields={fields} title="ویرایش" buttonName="ویرایش" setShowModal={setShowEditModal} data={selectedItem} reCheck={reloadKey} setRecheck={refresh} />}
    {showDeleteModal && <DeleteModalWriters title="حذف رشته از دانشکده" buttonName="حذف" text="آیا از حذف این رشته از دانشکده اطمینان دارید؟" setShowModal={setShowDeleteModal} api={`CollegeField/DeleteCollegeField/${selectedItem?.collegeFieldId}`} reCheck={reloadKey} setRecheck={refresh} />}
  </div>;
};

export default MainAdminCollegeFields;
